import { UserRole, RoleLayer } from '../enums/role.enum';
import { Permission } from '../enums/permission.enum';
import { RolePermissionMapping } from '../interfaces/rbac.interface';

export const ROLE_PERMISSION_MAPPINGS: RolePermissionMapping[] = [
  // All permissions (admin has everything)
  {
    role: UserRole.ACCOUNT_ADMIN,
    layer: RoleLayer.ACCOUNT,
    permissions: [
      ...Object.values(Permission),
    ],
  },

  // Domain Admin - Full access within their domain(s)
  {
    role: UserRole.DOMAIN_ADMIN,
    layer: RoleLayer.DOMAIN,
    permissions: [
      Permission.LINKS_VIEW,
      Permission.LINKS_VIEW_ALL,
      Permission.LINKS_CREATE,
      Permission.LINKS_EDIT,
      Permission.LINKS_DELETE,
      Permission.LINKS_EXPORT,

      Permission.GROUPS_VIEW,
      Permission.GROUPS_CREATE,
      Permission.GROUPS_EDIT,
      Permission.GROUPS_DELETE,

      Permission.USERS_VIEW,
      Permission.USERS_CREATE,
      Permission.USERS_EDIT,
      Permission.USERS_DELETE,
      Permission.USERS_MANAGE_ROLES,

      Permission.ATTRIBUTES_VIEW,
      Permission.ATTRIBUTES_CREATE,
      Permission.ATTRIBUTES_EDIT,
      Permission.ATTRIBUTES_DELETE,

      Permission.LINK_TEMPLATES_VIEW,
      Permission.LINK_TEMPLATES_CREATE,
      Permission.LINK_TEMPLATES_EDIT,
      Permission.LINK_TEMPLATES_DELETE,

      Permission.QR_TEMPLATES_VIEW,
      Permission.QR_TEMPLATES_CREATE,
      Permission.QR_TEMPLATES_EDIT,
      Permission.QR_TEMPLATES_DELETE,

      Permission.TRUSTED_DESTINATIONS_VIEW,
      Permission.TRUSTED_DESTINATIONS_CREATE,
      Permission.TRUSTED_DESTINATIONS_EDIT,
      Permission.TRUSTED_DESTINATIONS_DELETE,

      Permission.PENDING_LINKS_VIEW,
      Permission.PENDING_LINKS_APPROVE,
      Permission.PENDING_LINKS_REJECT,

      Permission.IP_EXCLUSIONS_VIEW,
      Permission.IP_EXCLUSIONS_CREATE,
      Permission.IP_EXCLUSIONS_EDIT,
      Permission.IP_EXCLUSIONS_DELETE,

      Permission.TAGS_VIEW,
      Permission.TAGS_CREATE,
      Permission.TAGS_EDIT,
      Permission.TAGS_DELETE,

      Permission.POWER_PAGES_VIEW,
      Permission.POWER_PAGES_CREATE,
      Permission.POWER_PAGES_EDIT,
      Permission.POWER_PAGES_DELETE,

      Permission.ANALYTICS_VIEW,
      Permission.ANALYTICS_EXPORT,
      Permission.REPORTS_VIEW,
      Permission.REPORTS_CREATE,

      Permission.DOMAIN_VIEW,
      Permission.DOMAIN_EDIT,
      Permission.DOMAIN_SETTINGS,

      Permission.AUDIT_LOGS_VIEW,

      Permission.E_LINKS_VIEW,
    ],
  },

  // Reporting User - Read-only access
  {
    role: UserRole.REPORTING_USER,
    layer: RoleLayer.DOMAIN,
    permissions: [
      // View-only permissions
      Permission.LINKS_VIEW,
      Permission.LINKS_VIEW_ALL,
      Permission.GROUPS_VIEW,
      Permission.ATTRIBUTES_VIEW,
      Permission.LINK_TEMPLATES_VIEW,
      Permission.QR_TEMPLATES_VIEW,
      Permission.TRUSTED_DESTINATIONS_VIEW,
      Permission.PENDING_LINKS_VIEW,
      Permission.IP_EXCLUSIONS_VIEW,
      Permission.TAGS_VIEW,
      Permission.POWER_PAGES_VIEW,
      Permission.ANALYTICS_VIEW,
      Permission.REPORTS_VIEW,
      Permission.DOMAIN_VIEW,

      Permission.E_LINKS_VIEW,
    ],
  },

  // Link Admin - Link management + view all links
  {
    role: UserRole.LINK_ADMIN,
    layer: RoleLayer.DOMAIN,
    permissions: [
      Permission.LINKS_VIEW,
      Permission.LINKS_VIEW_ALL,
      Permission.LINKS_CREATE,
      Permission.LINKS_EDIT,
      Permission.LINKS_DELETE,
      Permission.LINKS_EXPORT,

      Permission.GROUPS_VIEW,
      Permission.GROUPS_CREATE,
      Permission.GROUPS_EDIT,
      Permission.GROUPS_DELETE,

      Permission.QR_TEMPLATES_VIEW,

      Permission.LINK_TEMPLATES_VIEW,

      Permission.TAGS_VIEW,
      Permission.TAGS_CREATE,
      Permission.TAGS_EDIT,
      Permission.TAGS_DELETE,

      Permission.POWER_PAGES_VIEW,
      Permission.POWER_PAGES_CREATE,
      Permission.POWER_PAGES_EDIT,
      Permission.POWER_PAGES_DELETE,

      Permission.ANALYTICS_VIEW,
      Permission.REPORTS_VIEW,

      Permission.DOMAIN_VIEW,

      Permission.E_LINKS_VIEW,
    ],
  },

  {
    role: UserRole.QR_MANAGER,
    layer: RoleLayer.DOMAIN,
    permissions: [
      Permission.LINKS_VIEW,

      Permission.ATTRIBUTES_VIEW,

      Permission.QR_TEMPLATES_VIEW,
      Permission.QR_TEMPLATES_CREATE,
      Permission.QR_TEMPLATES_EDIT,
      Permission.QR_TEMPLATES_DELETE,
    ],
  },

  // Standard User - Basic permissions
  {
    role: UserRole.STANDARD_USER,
    layer: RoleLayer.DOMAIN,
    permissions: [
      // Links - Basic operations (can only see own links)
      Permission.LINKS_VIEW,
      Permission.LINKS_CREATE,
      Permission.LINKS_EDIT,
      Permission.LINKS_DELETE,

      Permission.GROUPS_VIEW,
      Permission.GROUPS_CREATE,

      Permission.TAGS_VIEW,
      Permission.TAGS_CREATE,

      Permission.ANALYTICS_VIEW,
    ],
  },

  // No Access - No permissions
  {
    role: UserRole.NO_ACCESS,
    layer: RoleLayer.DOMAIN,
    permissions: [],
  },
];

export function getPermissionsForRole(role: UserRole): Permission[] {
  const mapping = ROLE_PERMISSION_MAPPINGS.find(m => m.role === role);

  return mapping ? [...mapping.permissions] : [];
}

export function getRolesWithPermission(permission: Permission): UserRole[] {
  return ROLE_PERMISSION_MAPPINGS
    .filter(mapping => mapping.permissions.includes(permission))
    .map(mapping => mapping.role);
}

export function roleHasPermission(role: UserRole, permission: Permission): boolean {
  const permissions = getPermissionsForRole(role);

  return permissions.includes(permission);
}

export function combinePermissions(roles: UserRole[]): Permission[] {
  const allPermissions = roles.flatMap(role => getPermissionsForRole(role));

  return [...new Set(allPermissions)]; // Remove duplicates
}
